<template>
  <router-view v-if="!showShell" :key="$route.fullPath" />

  <div v-else id="app" class="min-h-screen bg-base text-gray-100 flex">

    <!-- ── Overlay mobile ─────────────────────────────── -->
    <transition name="overlay-fade">
      <div v-if="mobileOpen"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden"
        @click="mobileOpen = false">
      </div>
    </transition>

    <!-- ── Sidebar ────────────────────────────────────── -->
    <aside
      class="app-sidebar fixed h-full z-40 flex flex-col transition-all duration-300 overflow-hidden"
      :class="[
        collapsed ? 'w-[68px]' : 'w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      ]"
    >
      <!-- Logo -->
      <div class="flex items-center border-b border-white/[0.06]"
        :class="collapsed ? 'justify-center p-3' : 'px-4 py-3.5 gap-3'">
        <div class="w-9 h-9 flex-shrink-0 bg-gradient-to-br from-cyan-500 to-violet-600 rounded-xl flex items-center justify-center font-bold text-[10px] tracking-tight shadow-lg shadow-cyan-500/20 text-white">
          BIA
        </div>
        <div v-if="!collapsed" class="min-w-0 leading-tight">
          <div class="text-sm font-bold text-white tracking-wide">BetIA <span class="text-cyan-400">Terminal</span></div>
          <div class="text-[10px] text-gray-500 font-medium uppercase tracking-widest">Polymarket</div>
        </div>
        <button v-if="!collapsed" @click="mobileOpen = false"
          class="ml-auto p-1 rounded-lg text-gray-500 hover:text-white md:hidden flex-shrink-0" title="Fechar menu">
          ✕
        </button>
      </div>

      <!-- Navegação -->
      <nav class="flex-1 py-4 px-2.5 overflow-y-auto overflow-x-hidden custom-scrollbar">
        <div v-for="(group, gi) in navGroups" :key="group.title" :class="gi > 0 ? 'mt-5' : ''">
          <p v-if="!collapsed" class="px-2.5 mb-2 text-[10px] font-bold text-gray-600 uppercase tracking-[0.15em]">
            {{ group.title }}
          </p>
          <div v-else-if="gi > 0" class="mx-2 mb-3 border-t border-white/[0.06]"></div>

          <div class="space-y-1">
            <router-link
              v-for="(link, li) in group.links" :key="link.to"
              :to="link.to"
              class="nav-item stagger-item group relative flex items-center gap-3 rounded-xl py-2.5 transition-all duration-200"
              :style="{ '--i': gi * 4 + li, '--glow-rgb': link.glowRgb }"
              :class="[
                isActive(link.to)
                  ? `nav-item--active ${link.activeText}`
                  : 'text-gray-400 hover:text-gray-100 hover:bg-white/[0.05]',
                collapsed ? 'justify-center px-2' : 'px-3'
              ]"
              :title="link.label"
              @click="mobileOpen = false"
            >
              <!-- barra lateral do acento -->
              <span
                class="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full transition-all duration-300"
                :class="isActive(link.to) ? `${link.barClass} opacity-100` : 'opacity-0 bg-transparent'"
              ></span>

              <span class="relative text-lg leading-none flex-shrink-0 transition-transform duration-200 group-hover:scale-110">
                {{ link.icon }}
                <span v-if="link.to === '/claims' && pendingClaimsCount > 0"
                  class="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-0.5 bg-amber-500 text-black text-[9px] font-bold rounded-full flex items-center justify-center leading-none font-mono tabular-nums">
                  {{ pendingClaimsCount > 9 ? '9+' : pendingClaimsCount }}
                </span>
              </span>

              <span v-if="!collapsed" class="text-sm font-semibold tracking-wide truncate">{{ link.label }}</span>

              <span v-if="!collapsed && link.to === '/claims' && pendingClaimsCount > 0"
                class="ml-auto bg-amber-500/15 text-amber-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none font-mono tabular-nums">
                {{ pendingClaimsCount }}
              </span>
            </router-link>
          </div>
        </div>
      </nav>

      <!-- Rodapé da sidebar: usuário + colapsar -->
      <div class="border-t border-white/[0.06] p-2.5 space-y-1.5">
        <div v-if="!collapsed" class="flex items-center gap-2.5 px-2 py-1.5 rounded-xl">
          <div class="w-7 h-7 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-[11px] font-bold text-cyan-300 uppercase flex-shrink-0">
            {{ (authStore.user?.username || '?').slice(0, 2) }}
          </div>
          <div class="min-w-0 leading-tight flex-1">
            <p class="text-xs font-semibold text-white truncate">{{ authStore.user?.username }}</p>
            <p class="text-[10px] text-gray-500 uppercase">{{ authStore.user?.role }}</p>
          </div>
          <button @click="handleLogout" title="Sair"
            class="p-1.5 rounded-lg text-gray-500 hover:text-rose-400 hover:bg-rose-500/10 transition-all text-sm">
            ⏻
          </button>
        </div>
        <button v-else @click="handleLogout" title="Sair"
          class="w-full flex justify-center p-2 rounded-lg text-gray-500 hover:text-rose-400 hover:bg-rose-500/10 transition-all text-sm">
          ⏻
        </button>

        <button @click="toggleCollapse"
          class="w-full flex items-center justify-center gap-2 rounded-xl p-2 text-gray-600 hover:text-gray-200 hover:bg-white/[0.05] transition-all text-xs font-medium"
          :title="collapsed ? 'Expandir menu' : 'Recolher menu'"
        >
          <span class="transition-transform duration-300" :class="collapsed ? 'rotate-180' : ''">◀</span>
          <span v-if="!collapsed">Recolher</span>
        </button>
      </div>
    </aside>

    <!-- ── Conteúdo ────────────────────────────────────── -->
    <div class="flex-1 flex flex-col min-h-screen transition-all duration-300"
      :class="collapsed ? 'md:ml-[68px]' : 'md:ml-60'">

      <!-- Topbar -->
      <header class="app-topbar sticky top-0 z-30">
        <div class="px-4 md:px-6 py-3 flex items-center gap-3 md:gap-5">

          <!-- Hamburger (mobile) -->
          <button @click="mobileOpen = true"
            class="md:hidden p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/[0.06] transition flex-shrink-0">
            ☰
          </button>

          <!-- Título da página -->
          <div class="flex-1 min-w-0">
            <h2 class="text-base md:text-lg font-bold text-white truncate tracking-wide">{{ currentPageTitle }}</h2>
            <p class="text-[11px] text-gray-500 hidden sm:block">Trading automatizado · Polymarket</p>
          </div>

          <!-- Saldo (ao vivo) -->
          <div
            class="hidden sm:flex flex-col items-end px-3 py-1 rounded-xl transition-all duration-300"
            :class="{ 'pulse-glow': balanceFlash }"
            style="--glow-rgb: 52, 211, 153;"
          >
            <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Saldo</span>
            <span class="text-base font-bold text-emerald-300 leading-tight">
              <AnimatedNumber :value="walletBalance" :decimals="2" prefix="$ " />
            </span>
          </div>

          <!-- Ordens ativas -->
          <div class="hidden lg:flex flex-col items-end">
            <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Ordens</span>
            <span class="text-base font-bold text-amber-300 leading-tight font-mono tabular-nums">{{ botStatus.activeOrders || 0 }}</span>
          </div>

          <!-- Status do bot -->
          <div class="hidden sm:flex items-center px-3 py-2 rounded-xl border border-white/[0.06] bg-white/[0.03]">
            <LiveDot :active="!!botStatus.isRunning" :label="botStatus.isRunning ? 'Rodando' : 'Parado'" />
          </div>

          <!-- Start / Stop -->
          <button @click="toggleBot"
            class="px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-bold transition-all duration-200 border whitespace-nowrap hover:-translate-y-0.5"
            :class="botStatus.isRunning
              ? 'bg-rose-500/15 text-rose-400 border-rose-500/30 hover:bg-rose-500/25 hover:shadow-lg hover:shadow-rose-500/20'
              : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25 hover:shadow-lg hover:shadow-emerald-500/20'"
          >
            <span class="hidden sm:inline">{{ botStatus.isRunning ? '⏹ Parar' : '▶ Iniciar' }}</span>
            <span class="sm:hidden">{{ botStatus.isRunning ? '⏹' : '▶' }}</span>
          </button>

          <!-- Toggle tema -->
          <button @click="themeStore.toggle()"
            class="p-2 rounded-xl transition-all border border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.06] text-gray-400 hover:text-white text-sm"
            :title="themeStore.isDark ? 'Modo claro' : 'Modo escuro'">
            {{ themeStore.isDark ? '☀️' : '🌙' }}
          </button>
        </div>
      </header>

      <!-- Banner modo teste -->
      <div v-if="botStatus.testMode"
        class="bg-amber-500/10 border-b border-amber-500/25 px-4 md:px-6 py-2 flex items-center gap-2">
        <span class="text-amber-400">⚠️</span>
        <p class="text-xs font-semibold text-amber-400">MODO TESTE ATIVO — Nenhum trade real será executado</p>
      </div>

      <!-- Conteúdo da página -->
      <main class="flex-1 w-full max-w-[1600px] mx-auto p-6">
        <router-view v-slot="{ Component }">
          <transition name="fade-slide-up" mode="out-in">
            <component :is="Component" :key="$route.fullPath" />
          </transition>
        </router-view>
      </main>

      <!-- Rodapé -->
      <footer class="border-t border-white/[0.06] px-4 md:px-8 py-3">
        <p class="text-[11px] text-gray-600 text-center">BetIA Terminal — Sistema de Trading Automatizado</p>
      </footer>
    </div>

    <!-- Toasts -->
    <div class="fixed bottom-4 right-4 space-y-3 z-50 max-w-xs w-full">
      <transition-group name="toast">
        <div v-for="notification in notifications" :key="notification.id"
          class="px-4 py-3 rounded-2xl text-sm font-medium shadow-2xl backdrop-blur-md border"
          :class="{
            'bg-emerald-500/15 text-emerald-300 border-emerald-500/30': notification.type === 'success',
            'bg-rose-500/15 text-rose-300 border-rose-500/30': notification.type === 'error',
            'bg-cyan-500/15 text-cyan-300 border-cyan-500/30': notification.type === 'info'
          }">
          {{ notification.message }}
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import { useBotStore } from '../stores/botStore';
import { authService } from '../services/authService';
import { getWsUrl, getApiBaseUrl } from '../config/runtime';
import AnimatedNumber from '@/components/ui/AnimatedNumber.vue';
import LiveDot from '@/components/ui/LiveDot.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const themeStore = useThemeStore();
const botStore = useBotStore();

// ── Sidebar ────────────────────────────────────────────────
const collapsed = ref(localStorage.getItem('sidebar_collapsed') === 'true');
const mobileOpen = ref(false);

function toggleCollapse() {
  collapsed.value = !collapsed.value;
  localStorage.setItem('sidebar_collapsed', collapsed.value);
}

watch(() => route.path, () => { mobileOpen.value = false; });

const navGroups = [
  {
    title: 'Mercados',
    links: [
      { to: '/ao-vivo', label: 'Ao Vivo', icon: '🔴', glowRgb: '244, 63, 94', barClass: 'bg-rose-500', activeText: 'text-rose-300' },
      { to: '/', label: 'Visão Geral', icon: '🏠', glowRgb: '56, 189, 248', barClass: 'bg-sky-400', activeText: 'text-sky-300' },
      { to: '/crypto', label: 'Crypto', icon: '⚡', glowRgb: '34, 211, 238', barClass: 'bg-cyan-400', activeText: 'text-cyan-300' },
      { to: '/simulacoes', label: 'Simulações', icon: '🧪', glowRgb: '251, 191, 36', barClass: 'bg-amber-400', activeText: 'text-amber-300' },
      { to: '/futebol', label: 'Futebol', icon: '⚽', glowRgb: '52, 211, 153', barClass: 'bg-emerald-400', activeText: 'text-emerald-300' },
      { to: '/tweets', label: 'Tweets', icon: '🐦', glowRgb: '167, 139, 250', barClass: 'bg-violet-400', activeText: 'text-violet-300' },
    ],
  },
  {
    title: 'Gestão',
    links: [
      { to: '/trades', label: 'Trades', icon: '📜', glowRgb: '148, 163, 184', barClass: 'bg-gray-300', activeText: 'text-white' },
      { to: '/claims', label: 'Claims', icon: '💰', glowRgb: '251, 191, 36', barClass: 'bg-amber-400', activeText: 'text-amber-300' },
      { to: '/metas', label: 'Metas', icon: '🎯', glowRgb: '148, 163, 184', barClass: 'bg-gray-300', activeText: 'text-white' },
      { to: '/settings', label: 'Config', icon: '⚙️', glowRgb: '148, 163, 184', barClass: 'bg-gray-300', activeText: 'text-white' },
    ],
  },
];

function isActive(to) {
  return route.path === to;
}

// ── Estado do bot / dados ao vivo ──────────────────────────
const botStatus = ref({ isRunning: false, lastAnalysis: null, activeOrders: 0 });
const pendingClaimsCount = ref(0);
const notifications = ref([]);
const balanceFlash = ref(false);
let ws = null;
let claimsInterval = null;
let walletInterval = null;
let notificationListener = null;
let balanceFlashTimeout = null;

const walletBalance = computed(() => Number(botStore.bankSnapshot.balance) || 0);

// Pulso no saldo quando o valor muda
watch(walletBalance, (nv, ov) => {
  if (nv === ov) return;
  balanceFlash.value = true;
  if (balanceFlashTimeout) clearTimeout(balanceFlashTimeout);
  balanceFlashTimeout = setTimeout(() => { balanceFlash.value = false; }, 2000);
});

const fetchPendingClaims = async () => {
  try {
    const headers = authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
    const res = await fetch(`${getApiBaseUrl()}/claims?limit=200`, { headers });
    if (res.ok) {
      const data = await res.json();
      pendingClaimsCount.value = (data.claims || []).filter(c => c.status === 'PENDING').length;
    }
  } catch (_) {}
};

const fetchWalletBalance = async () => {
  try {
    const headers = authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
    const res = await fetch(`${getApiBaseUrl()}/v1/wallet/summary`, { headers });
    if (res.ok) {
      const data = await res.json();
      const balance = Number(data.total ?? data.balance ?? 0);
      if (balance) botStore.setBankSnapshot({ balance, profit: Number(data.profit ?? 0) });
    }
  } catch (_) {}
};

const pageTitles = {
  '/ao-vivo': 'Operação Ao Vivo',
  '/': 'Visão Geral',
  '/crypto': 'Crypto',
  '/futebol': 'Futebol · Combos',
  '/tweets': 'Tweets',
  '/trades': 'Histórico de Trades',
  '/claims': 'Claims',
  '/analyses': 'Análises de IA',
  '/simulacoes': 'Laboratório de Simulações',
  '/metas': 'Metas',
  '/weather': 'Clima',
  '/settings': 'Configurações',
};

const currentPageTitle = computed(() => pageTitles[route.path] || 'Visão Geral');
const showShell = computed(() => route.name !== 'Login');

// ── WebSocket (reconexão automática) ───────────────────────
const initWebSocket = () => {
  if (!showShell.value || ws) return;
  ws = new WebSocket(getWsUrl());
  ws.onopen = () => console.log('WebSocket conectado');
  ws.onmessage = (event) => {
    try { handleWebSocketMessage(JSON.parse(event.data)); } catch (_) {}
    // Alimenta o store global (saldo, trades, métricas em tempo real)
    botStore.onWebSocketMessage(event);
  };
  ws.onerror = (error) => console.error('Erro WebSocket:', error);
  ws.onclose = () => { console.log('WebSocket desconectado'); setTimeout(initWebSocket, 5000); };
};

const destroyWebSocket = () => {
  if (ws) { ws.onclose = null; ws.close(); ws = null; }
};

const handleWebSocketMessage = ({ type, data }) => {
  if (type === 'status:updated' && !Object.prototype.hasOwnProperty.call(data || {}, 'runningWorkers')) {
    botStatus.value = data;
  } else if (type === 'status:worker' && data?.userId === authStore.user?.user_id) {
    botStatus.value = data;
  }
};

// ── Ações ──────────────────────────────────────────────────
const toggleBot = async () => {
  try {
    const data = botStatus.value.isRunning ? await authService.stopBot() : await authService.startBot();
    botStatus.value = data.status;
    addNotification(data.status?.isRunning ? 'Bot iniciado' : 'Bot parado', 'success');
  } catch (error) {
    addNotification(error.response?.data?.error || 'Erro ao alternar bot', 'error');
  }
};

const addNotification = (message, type = 'info') => {
  const id = Date.now();
  notifications.value.push({ id, message, type });
  setTimeout(() => { notifications.value = notifications.value.filter(n => n.id !== id); }, 3000);
};

const handleLogout = async () => {
  authStore.logout();
  addNotification('Desconectado com sucesso', 'info');
  router.push('/login');
};

// ── Ciclo de vida ──────────────────────────────────────────
const startShell = () => {
  initWebSocket();
  authService.getBotStatus()
    .then((data) => { if (data?.status) botStatus.value = data.status; })
    .catch((error) => console.error('Erro ao carregar status do bot:', error));
  fetchPendingClaims();
  fetchWalletBalance();
  if (!claimsInterval) claimsInterval = setInterval(fetchPendingClaims, 60000);
  if (!walletInterval) walletInterval = setInterval(fetchWalletBalance, 30000);
};

const stopShell = () => {
  destroyWebSocket();
  if (claimsInterval) { clearInterval(claimsInterval); claimsInterval = null; }
  if (walletInterval) { clearInterval(walletInterval); walletInterval = null; }
};

onMounted(() => {
  notificationListener = (event) => {
    const detail = event?.detail || {};
    if (detail.message) addNotification(detail.message, detail.type || 'info');
  };
  window.addEventListener('app:notify', notificationListener);

  if (showShell.value) startShell();
});

watch(showShell, (enabled) => {
  if (enabled) startShell();
  else stopShell();
});

onUnmounted(() => {
  stopShell();
  if (balanceFlashTimeout) clearTimeout(balanceFlashTimeout);
  if (notificationListener) {
    window.removeEventListener('app:notify', notificationListener);
    notificationListener = null;
  }
});
</script>

<style>
/* ── Shell: sidebar / topbar (dark-first + override .light) ── */
.app-sidebar {
  background: rgba(10, 15, 28, 0.92);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.app-topbar {
  background: rgba(7, 11, 20, 0.82);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
html.light .app-sidebar {
  background: rgba(255, 255, 255, 0.96);
  border-right-color: #e2e2e8;
}
html.light .app-topbar {
  background: rgba(255, 255, 255, 0.92);
  border-bottom-color: #e2e2e8;
}

/* Item ativo da navegação: fundo glass + glow do acento */
.nav-item { border: 1px solid transparent; }
.nav-item--active {
  background: rgba(var(--glow-rgb), 0.10);
  border-color: rgba(var(--glow-rgb), 0.25);
  box-shadow: 0 0 16px -4px rgba(var(--glow-rgb), 0.35);
}
html.light .nav-item--active {
  background: rgba(var(--glow-rgb), 0.10);
}

/* Overlay mobile */
.overlay-fade-enter-active,
.overlay-fade-leave-active { transition: opacity 0.25s ease; }
.overlay-fade-enter-from,
.overlay-fade-leave-to { opacity: 0; }

/* Toasts */
.toast-enter-active,
.toast-leave-active { transition: all 0.3s ease; }
.toast-enter-from,
.toast-leave-to { opacity: 0; transform: translateX(20px); }
</style>
